import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Project, ProjectSummary } from '@/types'

import { useRef } from 'react'
import { toPlatform } from '@/lib/platforms'
import { getProject, getProjectSlugs } from '@/lib/content'
import { useScrollFrame } from '@/hooks/use-scroll-frame'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { CountUp } from '@/components/kit/count-up'
import { PageLink } from '@/components/kit/page-link'
import { Photo } from '@/components/kit/photo'
import { PlatformIcon } from '@/components/kit/platform-icon'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { Spark } from '@/components/kit/spark'
import { GrowthChart } from '@/components/work/growth-chart'
import { NextProject } from '@/components/work/next-project'
import { PhoneGallery } from '@/components/work/phone-gallery'

interface CaseStudyProps {
    project: Project
    next: ProjectSummary | null
}

const CoverHero: FC<{ project: Project }> = ({ project }) => {
    const imageRef = useRef<HTMLDivElement>(null)

    useScrollFrame(() => {
        const image = imageRef.current

        if (!image || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const y = Math.min(window.scrollY, window.innerHeight * 1.2)

        image.style.transform = `translate3d(0, ${(y * 0.32).toFixed(1)}px, 0) scale(${(1 + y * 0.00018).toFixed(4)})`
    })

    return (
        <section
            aria-labelledby='case-title'
            className='relative h-[100svh] min-h-[620px] overflow-hidden bg-ink text-cream'
        >
            <div
                ref={imageRef}
                className='absolute inset-0 will-change-transform'
            >
                {project.cover && (
                    <Photo
                        src={project.cover}
                        alt={project.cover_alt ?? ''}
                        sizes='100vw'
                        preload
                        grain
                        className='absolute inset-0 bg-ink'
                    />
                )}
            </div>

            <div className='absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/45' />

            <div className='relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-5 pb-12 md:px-10 md:pb-16'>
                <Reveal>
                    <nav
                        aria-label='Breadcrumb'
                        className='flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-cream/75'
                    >
                        <PageLink
                            href='/work'
                            className='underline decoration-ember decoration-2 underline-offset-4 transition-colors hover:text-cream'
                        >
                            Work
                        </PageLink>
                        <Spark className='size-2 text-ember' />
                        <span data-mdlfy-cms={`projects:${project.id}:industry`}>{project.industry}</span>
                    </nav>
                </Reveal>

                <Reveal delay={120}>
                    <h1
                        id='case-title'
                        className='mt-5 text-[clamp(3.5rem,12.5vw,11.5rem)] font-bold leading-[0.84] tracking-[-0.045em]'
                    >
                        <span data-mdlfy-cms={`projects:${project.id}:brand`}>{project.brand}</span>
                    </h1>
                </Reveal>

                <Reveal
                    delay={260}
                    className='mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between'
                >
                    {project.tagline && (
                        <p
                            className='max-w-xl text-xl leading-snug text-cream/85 md:text-2xl'
                            data-mdlfy-cms={`projects:${project.id}:tagline`}
                        >
                            {project.tagline}
                        </p>
                    )}

                    {project.headline_result && (
                        <p className='inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-ember py-2 pl-3 pr-5 text-base font-semibold text-ink md:self-auto'>
                            <Spark className='size-3.5' />
                            <span data-mdlfy-cms={`projects:${project.id}:headline_result`}>{project.headline_result}</span>
                        </p>
                    )}
                </Reveal>
            </div>
        </section>
    )
}

const CaseStudy: FC<CaseStudyProps> = ({ project, next }): ReactNode => {
    const description = project.tagline ?? project.summary ?? `How ${project.brand} caught the lift with Updraft.`

    return (
        <Layout>
            <Seo
                title={project.brand}
                description={description}
                ogImage={project.cover ?? undefined}
                type='article'
            />

            <CoverHero project={project} />

            <section
                aria-label='Project summary'
                className='mx-auto max-w-[1400px] px-5 py-16 md:px-10 md:py-24'
            >
                <Reveal>
                    <dl className='grid gap-px overflow-hidden rounded-[1.75rem] border border-black/5 bg-ink/[0.07] sm:grid-cols-2 lg:grid-cols-4'>
                        <div className='bg-paper p-6 md:p-7'>
                            <dt className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>The brand</dt>
                            <dd className='mt-3 text-xl font-semibold tracking-tight'>
                                <span data-mdlfy-cms={`projects:${project.id}:brand`}>{project.brand}</span>
                            </dd>
                        </div>

                        <div className='bg-paper p-6 md:p-7'>
                            <dt className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>What we did</dt>
                            <dd className='mt-3 flex flex-wrap gap-1.5'>
                                {project.services.length ? project.services.map(service => (
                                    <span
                                        key={service}
                                        className='rounded-full bg-sand px-3 py-1 text-sm'
                                    >
                                        {service}
                                    </span>
                                )) : <span className='text-stone'>The whole forecast</span>}
                            </dd>
                        </div>

                        <div className='bg-paper p-6 md:p-7'>
                            <dt className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>How long</dt>
                            <dd className='mt-3 text-xl font-semibold tracking-tight'>
                                {project.timeline
                                    ? <span data-mdlfy-cms={`projects:${project.id}:timeline`}>{project.timeline}</span>
                                    : <span className='text-stone'>Still running</span>}
                            </dd>
                        </div>

                        <div className='bg-paper p-6 md:p-7'>
                            <dt className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>Where</dt>
                            <dd className='mt-3 flex flex-wrap gap-2'>
                                {project.platforms.map(label => {
                                    const platform = toPlatform(label)

                                    return (
                                        <span
                                            key={label}
                                            className='inline-flex items-center gap-1.5 rounded-full border border-ink/10 px-3 py-1 text-sm'
                                        >
                                            {platform && (
                                                <PlatformIcon
                                                    name={platform}
                                                    className='size-3.5'
                                                />
                                            )}
                                            {label}
                                        </span>
                                    )
                                })}
                            </dd>
                        </div>
                    </dl>
                </Reveal>

                {project.summary && (
                    <Reveal delay={100}>
                        <p
                            className='mt-14 max-w-5xl font-heading text-[clamp(1.75rem,3.4vw,3rem)] font-bold leading-[1.1] tracking-[-0.035em] md:mt-20'
                            data-mdlfy-cms={`projects:${project.id}:summary`}
                        >
                            {project.summary}
                        </p>
                    </Reveal>
                )}
            </section>

            {(project.challenge || project.approach) && (
                <section
                    aria-label='Challenge and approach'
                    className='mx-auto grid max-w-[1400px] gap-12 px-5 pb-20 md:grid-cols-2 md:gap-16 md:px-10 md:pb-28'
                >
                    {project.challenge && (
                        <Reveal className='border-t border-ink/15 pt-8'>
                            <p className='font-mono text-xs text-stone'>[01]</p>
                            <h2 className='mt-4 text-[clamp(2rem,3.6vw,3rem)] font-bold tracking-[-0.035em]'>The low pressure</h2>
                            <p
                                className='mt-5 max-w-xl text-lg leading-relaxed text-stone'
                                data-mdlfy-cms={`projects:${project.id}:challenge`}
                            >
                                {project.challenge}
                            </p>
                        </Reveal>
                    )}

                    {project.approach && (
                        <Reveal
                            delay={120}
                            className='border-t border-ink/15 pt-8'
                        >
                            <p className='font-mono text-xs text-stone'>[02]</p>
                            <h2 className='mt-4 text-[clamp(2rem,3.6vw,3rem)] font-bold tracking-[-0.035em]'>The hunch we backed</h2>
                            <p
                                className='mt-5 max-w-xl text-lg leading-relaxed text-stone'
                                data-mdlfy-cms={`projects:${project.id}:approach`}
                            >
                                {project.approach}
                            </p>
                        </Reveal>
                    )}
                </section>
            )}

            <PhoneGallery project={project} />

            {(project.results.length > 0 || project.growth) && (
                <section
                    aria-labelledby='results-title'
                    className='grain grain-light relative bg-ink py-20 text-cream [--grain-opacity:0.25] md:py-28'
                >
                    <div className='relative mx-auto max-w-[1400px] px-5 md:px-10'>
                        <SectionLabel tone='cream'>The read</SectionLabel>
                        <h2
                            id='results-title'
                            className='mt-5 text-[clamp(2.5rem,6vw,5.5rem)] font-bold leading-[0.9] tracking-[-0.04em]'
                        >
                            What the <span className='font-serif font-normal italic tracking-[-0.01em]'>numbers</span> said
                        </h2>

                        {project.results.length > 0 && (
                            <div className='mt-12 grid gap-px overflow-hidden rounded-[1.75rem] bg-cream/10 sm:grid-cols-2 lg:grid-cols-4'>
                                {project.results.map((result, index) => (
                                    <Reveal
                                        key={result.label}
                                        delay={index * 90}
                                        className='bg-ink p-6 md:p-8'
                                    >
                                        <p className='font-heading text-[clamp(3rem,5.5vw,4.75rem)] font-bold leading-none tracking-[-0.04em]'>
                                            <CountUp
                                                value={result.value}
                                                decimals={result.decimals}
                                                prefix={result.prefix}
                                                suffix={result.suffix}
                                            />
                                        </p>
                                        <p className='mt-3 text-base text-cream/75'>{result.label}</p>
                                    </Reveal>
                                ))}
                            </div>
                        )}

                        {project.growth && (
                            <Reveal
                                delay={150}
                                className='mt-6'
                            >
                                <div className='rounded-[1.75rem] bg-paper p-5 text-ink md:p-10'>
                                    <GrowthChart growth={project.growth} />
                                </div>
                            </Reveal>
                        )}
                    </div>
                </section>
            )}

            {project.quote && (
                <section
                    aria-label='From the client'
                    className='mx-auto max-w-[1150px] px-5 py-24 text-center md:py-36'
                >
                    <Reveal>
                        <figure>
                            <Spark className='mx-auto size-8 text-ember' />
                            <blockquote className='mt-8 font-heading text-[clamp(2rem,4.6vw,4.25rem)] font-bold leading-[1.06] tracking-[-0.035em]'>
                                <p>
                                    “<span data-mdlfy-cms={`projects:${project.id}:quote`}>{project.quote}</span>”
                                </p>
                            </blockquote>
                            {(project.quote_author || project.quote_role) && (
                                <figcaption className='mt-10 flex flex-col items-center gap-1'>
                                    {project.quote_author && (
                                        <span
                                            className='text-lg font-semibold'
                                            data-mdlfy-cms={`projects:${project.id}:quote_author`}
                                        >
                                            {project.quote_author}
                                        </span>
                                    )}
                                    {project.quote_role && (
                                        <span
                                            className='text-stone'
                                            data-mdlfy-cms={`projects:${project.id}:quote_role`}
                                        >
                                            {project.quote_role}
                                        </span>
                                    )}
                                </figcaption>
                            )}
                        </figure>
                    </Reveal>
                </section>
            )}

            {next && <NextProject project={next} />}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const slugs = await getProjectSlugs()

        return { paths: slugs.map(slug => ({ params: { slug } })), fallback: 'blocking' }
    }
    catch (error) {
        console.error('Case study paths could not be loaded:', error instanceof Error ? error.message : error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<CaseStudyProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!slug) return { notFound: true, revalidate: 30 }

    try {
        const data = await getProject(slug)

        if (!data) return { notFound: true, revalidate: 30 }

        return { props: { project: data.project, next: data.next }, revalidate: 60 }
    }
    catch (error) {
        console.error(`Case study "${slug}" could not be loaded:`, error instanceof Error ? error.message : error)

        return { notFound: true, revalidate: 15 }
    }
}

export default CaseStudy
